import { useState } from "react";

const BROKERS = [
  "HSBC", "Goldman Sachs", "Morgan Stanley", "JP Morgan",
  "UBS", "BNP Paribas", "Citi", "Santander", "Banchile", "Other",
];

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "active")  return <span className="port-badge port-badge-active">Active</span>;
  if (s === "pending") return <span className="port-badge port-badge-setup">Pending Setup</span>;
  return <span className="port-badge port-badge-setup">{status || "—"}</span>;
}

export default function ManageBrokerModal({ isOpen, onClose, settings, onRemove, onAdd, accountName }) {
  const [broker,      setBroker]      = useState("");
  const [account,     setAccount]     = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [confirmId,   setConfirmId]   = useState(null);

  function handleClose() {
    setBroker("");
    setAccount("");
    setSubmitting(false);
    setConfirmId(null);
    onClose();
  }

  async function handleRequest() {
    if (!broker)         { alert("Please select a broker."); return; }
    if (!account.trim()) { alert("Please enter an account number."); return; }
    setSubmitting(true);
    try {
      await onAdd({ broker, account: account.trim() });
      setBroker("");
      setAccount("");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(id) {
    await onRemove(id);
    setConfirmId(null);
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) handleClose();
  }

  const activeCount  = settings.filter((s) => (s.status || "").toLowerCase() === "active").length;
  const pendingCount = settings.filter((s) => (s.status || "").toLowerCase() !== "active").length;

  return (
    <div className={`port-modal-overlay${isOpen ? " open" : ""}`} onMouseDown={handleOverlayClick}>
      <div className="port-modal">
        <div className="port-modal-hdr">
          <div>
            <div className="port-modal-title">Manage Broker Connections</div>
            <div className="port-modal-subtitle">{accountName} · Automated data feeds</div>
          </div>
          <button className="port-modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="port-modal-body">

          {/* Current connections */}
          <div>
            <div className="port-modal-section-label">Current Connections</div>
            {settings.length === 0 ? (
              <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.57rem", color: "var(--dash-muted)" }}>
                No connections configured.
              </div>
            ) : (
              <div className="port-broker-list">
                {settings.map((s) => (
                  <div key={s.id} className="port-broker-item">
                    <div>
                      <div className="port-broker-name">{s.broker}</div>
                      <div className="port-broker-acct">
                        {s.account}
                        {s.auth_expiry && s.auth_expiry !== "—"
                          ? ` · Auth expires ${s.auth_expiry}`
                          : " · Authorization pending"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <StatusBadge status={s.status} />
                      {confirmId === s.id ? (
                        <div style={{ display: "flex", gap: "0.3rem" }}>
                          <button className="port-del-cancel" onClick={() => setConfirmId(null)}>
                            Cancel
                          </button>
                          <button className="port-del-ok" onClick={() => handleRemove(s.id)}>
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button className="port-btn-icon" title="Remove" onClick={() => setConfirmId(s.id)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {settings.length > 0 && (
              <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.49rem", color: "var(--dash-muted)", marginTop: "0.4rem" }}>
                <strong style={{ color: "var(--dash-text2)" }}>{activeCount} active</strong>
                {pendingCount > 0 && ` · ${pendingCount} pending setup`}
              </div>
            )}
          </div>

          {/* Add new connection */}
          <div style={{ borderTop: "1px solid var(--dash-border)", paddingTop: "0.85rem", display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            <div className="port-modal-section-label">Add New Connection</div>

            <div className="port-step-row">
              <div className="port-step-num port-step-num-gold">1</div>
              <div className="port-step-body">
                <div className="port-step-title">Select broker</div>
                <div className="port-step-sub">Major custodians and prime brokers supported</div>
              </div>
              <select
                className="port-field-input"
                style={{ maxWidth: 185, flexShrink: 0 }}
                value={broker}
                onChange={(e) => setBroker(e.target.value)}
              >
                <option value="">Choose broker…</option>
                {BROKERS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>

            <div className="port-step-row">
              <div className="port-step-num port-step-num-blue">2</div>
              <div className="port-step-body">
                <div className="port-step-title">Enter account details</div>
                <div className="port-step-sub">Account number or reference</div>
              </div>
              <input
                className="port-field-input"
                type="text"
                placeholder="Account number"
                style={{ maxWidth: 185, flexShrink: 0 }}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
              />
            </div>

            <div className="port-step-row">
              <div className="port-step-num port-step-num-green">3</div>
              <div className="port-step-body">
                <div className="port-step-title">Request authorization</div>
                <div className="port-step-sub">We'll email a broker-specific authorization form to sign and return</div>
              </div>
              <button
                className="port-step-action port-step-action-green"
                onClick={handleRequest}
                disabled={submitting}
              >
                {submitting ? "…" : "↗ Request"}
              </button>
            </div>

            <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.49rem", color: "var(--dash-muted)", lineHeight: 1.6, borderTop: "1px solid var(--dash-border)", paddingTop: "0.6rem" }}>
              <strong style={{ color: "var(--dash-text2)" }}>Read-only access.</strong> Tail Risk Global never places trades or moves cash. We receive position snapshots and transaction history only.
            </div>
          </div>

        </div>

        <div className="port-modal-footer">
          <button className="port-btn port-btn-ghost" onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
